'use client';

import React, { useState } from 'react';

interface QuickTakeProps {
  theScoop: string;
  whyItMatters: string;
  title: string;
  articleUrl: string;
}

export default function QuickTake({ theScoop, whyItMatters, title, articleUrl }: QuickTakeProps) {
  const [copied, setCopied] = useState(false);

  const tweetText = encodeURIComponent(`${theScoop}\n\n➡️ Why It Matters: ${whyItMatters}\n\nFull analysis: ${articleUrl}\n\nvia @WorldPrimePost`);
  const tweetUrl = `https://twitter.com/intent/tweet?text=${tweetText}`;

  const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(articleUrl)}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`📰 The Scoop: ${theScoop}\n\n🎯 Why It Matters: ${whyItMatters}\n\n${articleUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div className="my-8 relative overflow-hidden rounded-xl border border-brand/20 bg-gradient-to-br from-brand/5 via-card to-card shadow-lg shadow-brand/5">
      {/* Top accent bar */}
      <div className="h-1 w-full bg-gradient-to-r from-brand via-brand/70 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-border/40">
        <div className="flex items-center gap-2.5">
          <div className="w-1.5 h-5 bg-brand rounded-full" />
          <span className="text-[10px] font-black uppercase tracking-[0.3em] text-brand">Quick Take</span>
          <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground/60 hidden sm:inline">• 30-Second Briefing</span>
        </div>
        {/* Share actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleCopy}
            title="Copy Quick Take"
            className="w-7 h-7 rounded-full border border-border/60 bg-background/80 flex items-center justify-center hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          >
            {copied ? (
              <svg className="w-3 h-3 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            ) : (
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
            )}
          </button>
          <a
            href={tweetUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="Share on X"
            className="w-7 h-7 rounded-full border border-border/60 bg-background/80 flex items-center justify-center hover:bg-black hover:text-white hover:border-black transition-all text-muted-foreground"
          >
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.004 3.985H5.078z" /></svg>
          </a>
          <a
            href={linkedInUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="Share on LinkedIn"
            className="w-7 h-7 rounded-full border border-border/60 bg-background/80 flex items-center justify-center hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2] transition-all text-muted-foreground"
          >
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
          </a>
        </div>
      </div>

      {/* Content: Two-column grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-border/30">
        {/* The Scoop */}
        <div className="p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-base">📰</span>
            <h3 className="text-[10px] font-black uppercase tracking-[0.25em] text-foreground">The Scoop</h3>
          </div>
          <p className="text-sm sm:text-base leading-relaxed text-foreground/90 font-serif">
            {theScoop}
          </p>
        </div>

        {/* Why It Matters */}
        <div className="p-5 sm:p-6 bg-brand/3">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-base">🎯</span>
            <h3 className="text-[10px] font-black uppercase tracking-[0.25em] text-brand">Why It Matters</h3>
          </div>
          <p className="text-sm sm:text-base leading-relaxed text-foreground/85 font-serif">
            {whyItMatters}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 py-2.5 border-t border-border/30 bg-muted/20 flex items-center justify-between">
        <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground/50">WorldPrimePost Intelligence Desk</span>
        <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground/50">Share This Insight →</span>
      </div>
    </div>
  );
}
